import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAIModel } from '../../contexts/AIModelContext';
import {
  Product,
  Revision,
  BOMItem,
  Phase,
  PhaseDisplayNames,
  PhaseColors,
  PartStatus,
  PartStatusColors,
  PartStatusDisplayNames,
} from '../../types/bom';
import {
  getProduct,
  listRevisions,
  getRevision,
  getTBDParts,
  listBuilds,
  listECNs,
  createProduct,
  updateRevision,
  getPartTypes,
  getPartCategories,
  getPartCodes,
  searchDigiKeyMLCC,
  batchCreatePartCodes,
  getDigiKeyPartTypes,
  getDigiKeyAIStatus,
  parseDigiKeySpecs,
  searchPartsOnline,
} from '../../services/bomService';
import type {
  PartTypeOption,
  PartCategory,
  PartCodeRecord,
  DigiKeyMLCCResult,
  DigiKeyMLCCResultGroup,
  DigiKeyPartType,
  DigiKeyParseResult,
  WebSearchPart,
  WebSearchResult,
} from '../../services/bomService';
import type { Build, ECN } from '../../types/bom';
import { isPCBPart, checkGerberAvailability } from '../../services/pcbService';
import { useProjects } from '../../hooks/useProject';
import PCBViewer from './PCBViewer';
import BOMImportDialog from './BOMImportDialog';
import { logger } from '@/utils/logger';

// Product categories
const PRODUCT_CATEGORIES = [
  { value: 'finished_good', label: 'Finished Good' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'pcba', label: 'PCBA' },
];

const ProductDetail: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedModel } = useAIModel();
  const isNewProduct = productId === 'new';

  // Determine the route prefix based on current path
  const getRoutePrefix = () => {
    const path = location.pathname;
    if (path.includes('/pcb-modules/')) {
      return '/app/bom/pcb-modules';
    } else if (path.includes('/assembled-modules/')) {
      return '/app/bom/assembled-modules';
    } else if (path.includes('/products/')) {
      return '/app/bom/products';
    }
    return '/app/bom/products'; // Default for legacy routes
  };
  const routePrefix = getRoutePrefix();
  const backUrl = routePrefix;

  const [product, setProduct] = useState<Product | null>(null);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [currentRevision, setCurrentRevision] = useState<Revision | null>(null);
  const [tbdParts, setTbdParts] = useState<BOMItem[]>([]);
  const [builds, setBuilds] = useState<Build[]>([]);
  const [ecns, setEcns] = useState<ECN[]>([]);
  const [activeTab, setActiveTab] = useState<'bom' | 'tbd' | 'builds' | 'ecn' | 'history'>('bom');
  const [loading, setLoading] = useState(!isNewProduct);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingItem, setDeletingItem] = useState<number | null>(null);

  // New product form state
  const [newProduct, setNewProduct] = useState({
    product_code: '',
    product_name: '',
    category: 'finished_good',
    project_code: '',
    target_mp_date: '',
  });

  // BOM Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Fetch projects for dropdown
  const { data: projectsData } = useProjects();
  const projects = projectsData?.projects || [];

  // Add Part modal state
  // Flow: Select Category -> Select Part Type (for electronic parts) -> Select Part -> Enter qty/ref/notes
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [addingPart, setAddingPart] = useState(false);
  const [partCategories, setPartCategories] = useState<PartCategory[]>([]);
  const [partTypes, setPartTypes] = useState<PartTypeOption[]>([]);
  const [availableParts, setAvailableParts] = useState<PartCodeRecord[]>([]);
  const [loadingParts, setLoadingParts] = useState(false);

  // Selected values for Add Part dialog
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPartType, setSelectedPartType] = useState('');
  const [selectedPart, setSelectedPart] = useState<PartCodeRecord | null>(null);
  const [partFilterText, setPartFilterText] = useState(''); // Filter for parts list

  // User-editable fields for Add Part
  const [newPartInput, setNewPartInput] = useState({
    qty_per: 1,
    unit: 'pcs',
    ref_designator: '',
    part_status: 'tbd' as PartStatus,
    notes: '',
  });

  // PCB Gerber Viewer state - separate modal dialog
  const [showPCBModal, setShowPCBModal] = useState(false);
  const [pcbPartNumber, setPcbPartNumber] = useState<string | null>(null);
  const [gerberAvailability, setGerberAvailability] = useState<Record<string, boolean>>({}); // Track which PCB parts have Gerber files

  // Add Part modal tab state: 'select' for existing parts, 'digikey' for DigiKey search
  const [addPartModalTab, setAddPartModalTab] = useState<'select' | 'digikey'>('select');

  // DigiKey search state
  const [digikeySearchInput, setDigikeySearchInput] = useState('');
  const [digikeySearchResults, setDigikeySearchResults] = useState<DigiKeyMLCCResultGroup[]>([]);
  const [digikeySearching, setDigikeySearching] = useState(false);
  const [digikeySearchError, setDigikeySearchError] = useState<string | null>(null);
  const [selectedDigikeyParts, setSelectedDigikeyParts] = useState<DigiKeyMLCCResult[]>([]);
  const [batchImporting, setBatchImporting] = useState(false);
  const [batchImportResult, setBatchImportResult] = useState<{success: number; total: number; errors?: string[]} | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('all');

  // DigiKey part type selection (from 電子零件別 sheet)
  const [digikeyPartTypes, setDigikeyPartTypes] = useState<DigiKeyPartType[]>([]);
  const [selectedDigikeyPartType, setSelectedDigikeyPartType] = useState<DigiKeyPartType | null>(null);
  const [loadingDigikeyPartTypes, setLoadingDigikeyPartTypes] = useState(false);

  // AI parsing state
  const [aiEnabled, setAiEnabled] = useState(false);
  const [useAIParse, setUseAIParse] = useState(false);
  const [parseResults, setParseResults] = useState<DigiKeyParseResult[]>([]);

  // AI Web Search state (fallback when DigiKey returns 0 results)
  const [webSearching, setWebSearching] = useState(false);
  const [webSearchResult, setWebSearchResult] = useState<WebSearchResult | null>(null);
  const [selectedWebParts, setSelectedWebParts] = useState<WebSearchPart[]>([]);
  const [showWebSearchConfirm, setShowWebSearchConfirm] = useState(false);
  const [hasZeroDigikeyResults, setHasZeroDigikeyResults] = useState(false);
  const [webSearchConfirmChecks, setWebSearchConfirmChecks] = useState({
    verified: false,
  });

  useEffect(() => {
    if (productId && !isNewProduct) {
      loadProduct();
    }
  }, [productId, isNewProduct]);

  // Load categories and part types when modal opens
  useEffect(() => {
    if (showAddPartModal) {
      if (partCategories.length === 0) {
        loadPartCategories();
      }
      if (partTypes.length === 0) {
        loadPartTypes();
      }
    }
  }, [showAddPartModal]);

  // Load DigiKey part types when DigiKey tab is active
  useEffect(() => {
    if (showAddPartModal && addPartModalTab === 'digikey' && digikeyPartTypes.length === 0) {
      loadDigikeyPartTypes();
    }
  }, [showAddPartModal, addPartModalTab]);

  // Load parts when category or part type changes
  useEffect(() => {
    if (selectedCategory) {
      loadAvailableParts();
    } else {
      setAvailableParts([]);
    }
    setSelectedPart(null);
  }, [selectedCategory, selectedPartType]);

  const loadPartCategories = async () => {
    try {
      const categories = await getPartCategories();
      setPartCategories(categories);
    } catch (err) {
      logger.error('Failed to load part categories:', err);
    }
  };

  const loadPartTypes = async () => {
    try {
      const types = await getPartTypes();
      setPartTypes(types);
    } catch (err) {
      logger.error('Failed to load part types:', err);
    }
  };

  const loadDigikeyPartTypes = async () => {
    try {
      setLoadingDigikeyPartTypes(true);
      const [types, aiStatus] = await Promise.all([
        getDigiKeyPartTypes(),
        getDigiKeyAIStatus().catch(() => ({ ai_enabled: false, ai_available: false })),
      ]);
      setDigikeyPartTypes(types);
      setAiEnabled(aiStatus.ai_enabled && aiStatus.ai_available);
      // Default to first type with DigiKey metadata (usually "C" for capacitor)
      const defaultType = types.find(t => t.digikey_category) || types[0];
      if (defaultType) {
        setSelectedDigikeyPartType(defaultType);
      }
    } catch (err) {
      logger.error('Failed to load DigiKey part types:', err);
    } finally {
      setLoadingDigikeyPartTypes(false);
    }
  };

  const loadAvailableParts = async () => {
    try {
      setLoadingParts(true);
      // Only use type filter for electronic_parts category
      const typeFilter = selectedCategory === 'electronic_parts' ? selectedPartType : undefined;
      const parts = await getPartCodes(selectedCategory, typeFilter);
      setAvailableParts(parts);

      // Check Gerber file availability for PCB parts
      const pcbPartNumbers = parts
        .filter(part => isPCBPart(part.part_number))
        .map(part => part.part_number);

      if (pcbPartNumbers.length > 0) {
        // Check availability in background (don't block UI)
        checkGerberAvailability(pcbPartNumbers).then(availability => {
          setGerberAvailability(prev => ({ ...prev, ...availability }));
        });
      }
    } catch (err) {
      logger.error('Failed to load parts:', err);
      setAvailableParts([]);
    } finally {
      setLoadingParts(false);
    }
  };

  const loadProduct = async () => {
    if (!productId) return;
    try {
      setLoading(true);
      setError(null);
      const [productData, revisionsData, tbdData, buildsData, ecnsData] = await Promise.all([
        getProduct(productId),
        listRevisions(productId),
        getTBDParts(productId),
        listBuilds(productId),
        listECNs(productId),
      ]);
      setProduct(productData);
      setRevisions(revisionsData || []);
      setTbdParts(tbdData || []);
      setBuilds(buildsData || []);
      setEcns(ecnsData || []);

      // Load current revision
      if (productData.current_revision) {
        const revData = await getRevision(productId, productData.current_revision);
        setCurrentRevision(revData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.product_code || !newProduct.product_name) {
      setError('Product code and name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Build request body - only send fields the backend needs
      const requestBody: Record<string, unknown> = {
        product_code: newProduct.product_code,
        product_name: newProduct.product_name,
        category: newProduct.category,
        current_phase: 'pre_bom',
        current_revision: 'A',
        status: 'active',
      };

      if (newProduct.project_code) {
        requestBody.project_code = newProduct.project_code;
      }

      // Convert date string to ISO 8601 format if provided
      if (newProduct.target_mp_date) {
        requestBody.target_mp_date = new Date(newProduct.target_mp_date).toISOString();
      }

      const created = await createProduct(requestBody as Partial<Product>);
      navigate(`/app/bom/products/${created.product_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPart = async () => {
    if (!selectedPart) {
      setError('Please select a part');
      return;
    }

    if (!currentRevision || !productId) {
      setError('No revision loaded');
      return;
    }

    try {
      setAddingPart(true);
      setError(null);

      // Calculate next line number
      const maxLineNo = currentRevision.items?.reduce((max, item) => Math.max(max, item.line_no), 0) || 0;

      // Build part type label from selected part type
      const partTypeLabel = selectedPartType
        ? partTypes.find(pt => pt.part_code === selectedPartType)?.label || selectedPartType
        : selectedPart.category;

      // Map selected part data to BOM item
      // Part details are pre-filled from Google Sheet:
      //   - part_code: 雲騰品號 (part_number)
      //   - description: 廠商品名 (vendor_product)
      //   - part_type: From 電子零件別 or 類別 field
      //   - specification: 廠商規格 (vendor_spec)
      //   - brand: 廠牌 (brand)
      const partToAdd: BOMItem = {
        line_no: maxLineNo + 1,
        level: 0,
        part_code: selectedPart.part_number,
        description: selectedPart.vendor_product || selectedPart.vendor_name,
        part_type: partTypeLabel,
        package_size: undefined, // Not in sheet, can be added later
        specification: selectedPart.vendor_spec,
        brand: selectedPart.brand,
        digikey_pn: undefined, // Not in sheet, can be added later
        qty_per: newPartInput.qty_per,
        unit: newPartInput.unit,
        ref_designator: newPartInput.ref_designator,
        is_assembly: false,
        is_phantom: false,
        part_status: newPartInput.part_status,
        notes: newPartInput.notes,
      };

      // Update revision with new item
      const updatedItems = [...(currentRevision.items || []), partToAdd];
      await updateRevision(productId, currentRevision.revision, {
        ...currentRevision,
        items: updatedItems,
      });

      // Reload the data
      await loadProduct();

      // Reset form and close modal
      resetAddPartForm();
      setShowAddPartModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add part');
    } finally {
      setAddingPart(false);
    }
  };

  const resetAddPartForm = () => {
    setSelectedCategory('');
    setSelectedPartType('');
    setSelectedPart(null);
    setAvailableParts([]);
    setPartFilterText(''); // Reset filter text
    setNewPartInput({
      qty_per: 1,
      unit: 'pcs',
      ref_designator: '',
      part_status: 'tbd',
      notes: '',
    });
    // Reset PCB viewer modal
    setShowPCBModal(false);
    setPcbPartNumber(null);
    setGerberAvailability({}); // Reset Gerber availability cache
    // Reset DigiKey search state
    setAddPartModalTab('select');
    setDigikeySearchInput('');
    setDigikeySearchResults([]);
    setDigikeySearchError(null);
    setSelectedDigikeyParts([]);
    setBatchImportResult(null);
    setSelectedManufacturer('all');
    // Reset DigiKey part type to default (first with metadata)
    const defaultType = digikeyPartTypes.find(t => t.digikey_category) || digikeyPartTypes[0];
    setSelectedDigikeyPartType(defaultType || null);
  };

  // Handle DigiKey search with optional AI parsing
  const handleDigikeySearch = async () => {
    if (!digikeySearchInput.trim()) {
      setDigikeySearchError(t('bom.digikey.enterSpecs', 'Please enter part specifications'));
      return;
    }

    try {
      setDigikeySearching(true);
      setDigikeySearchError(null);
      setDigikeySearchResults([]);
      setSelectedDigikeyParts([]);
      setBatchImportResult(null);
      setSelectedManufacturer('all');
      setParseResults([]);
      // Reset web search state
      setWebSearchResult(null);
      setSelectedWebParts([]);
      setHasZeroDigikeyResults(false);

      // Split input by newlines or commas
      const specs = digikeySearchInput
        .split(/[\n,]/)
        .map(s => s.trim())
        .filter(s => s.length > 0);

      if (specs.length === 0) {
        setDigikeySearchError(t('bom.digikey.enterAtLeastOne', 'Please enter at least one specification'));
        return;
      }

      // If AI parsing is enabled, parse first to show parsing results
      let searchResults: typeof digikeySearchResults = [];
      if (useAIParse && aiEnabled) {
        const parseResponse = await parseDigiKeySpecs({
          part_code: selectedDigikeyPartType?.part_code || 'C',
          texts: specs,
          use_ai_parse: true,
        });
        setParseResults(parseResponse.results);

        // Use parsed search queries for the actual search
        const searchSpecs = parseResponse.results.map(r => r.search_query);
        const response = await searchDigiKeyMLCC(searchSpecs);
        searchResults = response.results;
        setDigikeySearchResults(searchResults);
      } else {
        // Direct search without AI parsing
        const response = await searchDigiKeyMLCC(specs);
        searchResults = response.results;
        setDigikeySearchResults(searchResults);
      }

      // Check if all search results have zero products (use actual response, not stale state)
      const allZeroResults = searchResults.length > 0 && searchResults.every(r => r.products.length === 0);
      setHasZeroDigikeyResults(allZeroResults);
      if (allZeroResults) {
        setDigikeySearchError(t('bom.digikey.noResults', 'No results found for any specification'));
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setDigikeySearching(false);
    }
  };

  // Handle AI web search when DigiKey returns no results
  const handleWebSearch = async () => {
    if (!digikeySearchInput.trim() || !selectedDigikeyPartType) {
      return;
    }

    try {
      setWebSearching(true);
      setWebSearchResult(null);
      setSelectedWebParts([]);

      const spec = digikeySearchInput.trim().split(/[\n,]/)[0]?.trim() || digikeySearchInput.trim();
      const originalQuery = selectedDigikeyPartType.digikey_category
        ? `${selectedDigikeyPartType.digikey_category} ${spec}`
        : spec;

      const response = await searchPartsOnline(
        selectedDigikeyPartType.part_code,
        spec,
        originalQuery,
        'No results found',
        selectedModel.id  // Pass the user-selected AI model
      );

      setWebSearchResult(response.result);
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Web search failed');
    } finally {
      setWebSearching(false);
    }
  };

  // Toggle selection of a web search part
  const toggleWebPartSelection = (part: WebSearchPart) => {
    setSelectedWebParts(prev => {
      const isSelected = prev.some(p => p.manufacturer_pn === part.manufacturer_pn);
      if (isSelected) {
        return prev.filter(p => p.manufacturer_pn !== part.manufacturer_pn);
      } else {
        return [...prev, part];
      }
    });
  };

  // Handle adding selected web search parts (requires confirmation)
  const handleAddWebSearchParts = async () => {
    if (selectedWebParts.length === 0) return;

    // Show confirmation dialog
    setShowWebSearchConfirm(true);
    setWebSearchConfirmChecks({
      verified: false,
      pricingUnderstood: false,
      availabilityCheck: false,
    });
  };

  // Confirm and add web search parts to sheet
  const confirmAddWebSearchParts = async () => {
    if (!selectedDigikeyPartType) return;

    try {
      setBatchImporting(true);
      setShowWebSearchConfirm(false);

      // Convert web search parts to the format expected by batch create
      // The batch create expects the Google Sheets format
      const partsToAdd = selectedWebParts.map(part => ({
        part_number: part.manufacturer_pn,
        vendor_name: part.manufacturer,
        brand: part.manufacturer,
        vendor_product: part.description,
        vendor_spec: Object.entries(part.specifications || {}).map(([k, v]) => `${k}:${v}`).join(', '),
        category: selectedDigikeyPartType.part_code,
        dimension: part.specifications?.package || '',
        mpq: '',
      }));

      // Use batch create to add parts
      const result = await batchCreatePartCodes(
        selectedDigikeyPartType.part_code,
        partsToAdd
      );

      setBatchImportResult({
        success: result.success_count,
        total: result.total_count,
        errors: result.errors,
      });

      // Clear selections on success
      if (result.success_count > 0) {
        setSelectedWebParts([]);
        setWebSearchResult(null);
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Failed to add parts');
    } finally {
      setBatchImporting(false);
    }
  };

  // Toggle selection of a DigiKey part
  const toggleDigikeyPartSelection = (part: DigiKeyMLCCResult) => {
    setSelectedDigikeyParts(prev => {
      const isSelected = prev.some(p => p.manufacturer_part_number === part.manufacturer_part_number);
      if (isSelected) {
        return prev.filter(p => p.manufacturer_part_number !== part.manufacturer_part_number);
      } else {
        return [...prev, part];
      }
    });
  };

  // Check if a part is selected
  const isDigikeyPartSelected = (part: DigiKeyMLCCResult) => {
    return selectedDigikeyParts.some(p => p.manufacturer_part_number === part.manufacturer_part_number);
  };

  // Handle batch import of selected DigiKey parts to Google Sheet
  const handleBatchImport = async () => {
    if (selectedDigikeyParts.length === 0) {
      setDigikeySearchError('Please select at least one part to import');
      return;
    }

    try {
      setBatchImporting(true);
      setDigikeySearchError(null);

      // Convert selected parts to part code format
      // Map to the 電子零件 sheet structure
      const partCodes = selectedDigikeyParts.map(part => ({
        part_number: '', // Will be auto-generated or left empty for manual entry
        vendor_name: part.manufacturer_name,
        brand: part.manufacturer_name,
        vendor_product: part.manufacturer_part_number,
        vendor_spec: part.description,
        category: selectedDigikeyPartType?.part_code || 'C', // Use selected part type code
        dimension: part.package_size || '',
        mpq: '1', // Default MPQ
      }));

      const result = await batchCreatePartCodes('electronic_parts', partCodes);
      setBatchImportResult({
        success: result.success_count,
        total: result.total_count,
        errors: result.errors,
      });

      if (result.success_count > 0) {
        // Clear selection after successful import
        setSelectedDigikeyParts([]);
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBatchImporting(false);
    }
  };

  // Handle deleting a BOM item
  const handleDeleteItem = async (lineNo: number) => {
    if (!currentRevision || !productId) {
      setError('No revision loaded');
      return;
    }

    try {
      setDeletingItem(lineNo);
      setError(null);

      // Filter out the item to delete and renumber remaining items
      const updatedItems = currentRevision.items
        .filter((item) => item.line_no !== lineNo)
        .map((item, index) => ({
          ...item,
          line_no: index + 1, // Renumber sequentially
        }));

      await updateRevision(productId, currentRevision.revision, {
        ...currentRevision,
        items: updatedItems,
      });

      // Reload the data to get fresh state
      await loadProduct();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeletingItem(null);
    }
  };

  // Handle opening PCB viewer modal dialog
  const handleViewPCB = (partNumber: string) => {
    setPcbPartNumber(partNumber);
    setShowPCBModal(true);
  };

  // Handle closing PCB viewer modal
  const handleClosePCBModal = () => {
    setShowPCBModal(false);
  };

  // Render create form for new products
  if (isNewProduct) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <button
          onClick={() => navigate(backUrl)}
          className="text-sm text-blue-600 hover:underline mb-4"
        >
          &larr; {t('common.back', 'Back to Products')}
        </button>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          {t('bom.newProduct', 'New Product')}
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('bom.productCode', 'Product Code')} *
            </label>
            <input
              type="text"
              value={newProduct.product_code}
              onChange={(e) => setNewProduct({ ...newProduct, product_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., PROD-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('bom.productName', 'Product Name')} *
            </label>
            <input
              type="text"
              value={newProduct.product_name}
              onChange={(e) => setNewProduct({ ...newProduct, product_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., Smart Sensor Module"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('bom.category', 'Category')}
            </label>
            <select
              value={newProduct.category}
              onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {PRODUCT_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('bom.projectCode', 'Project Code')}
            </label>
            <select
              value={newProduct.project_code}
              onChange={(e) => setNewProduct({ ...newProduct, project_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">{t('bom.selectProject', 'Select a project (optional)')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.project_code}>
                  {project.project_code} - {project.project_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('bom.targetMPDate', 'Target MP Date')}
            </label>
            <input
              type="date"
              value={newProduct.target_mp_date}
              onChange={(e) => setNewProduct({ ...newProduct, target_mp_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCreateProduct}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.saving', 'Saving...') : t('common.create', 'Create Product')}
            </button>
            <button
              onClick={() => navigate('/app/bom')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600 py-8">{error || 'Product not found'}</div>
        <button
          onClick={() => navigate(backUrl)}
          className="mx-auto block px-4 py-2 text-blue-600 hover:underline"
        >
          {t('common.back', 'Back to Products')}
        </button>
      </div>
    );
  }

  const phases: Phase[] = ['pre_bom', 'evt', 'dvt', 'pvt', 'mp'];
  const currentPhaseIndex = phases.indexOf(product.current_phase);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <button
            onClick={() => navigate(backUrl)}
            className="text-sm text-blue-600 hover:underline mb-2"
          >
            &larr; {t('common.back', 'Back to Products')}
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {product.product_code} - {product.product_name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {product.category} | Rev {product.current_revision}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`${routePrefix}/${productId}/edit`)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('common.edit', 'Edit')}
          </button>
          <button
            onClick={() => setShowImportDialog(true)}
            className="px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/20"
          >
            {t('bom.importBOM', 'Import BOM')}
          </button>
          <button
            onClick={() => navigate(`${routePrefix}/${productId}/transition`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('bom.transition', 'Phase Transition')}
          </button>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          {phases.map((phase, index) => (
            <React.Fragment key={phase}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                    index <= currentPhaseIndex ? PhaseColors[phase] : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  {index + 1}
                </div>
                <span
                  className={`mt-2 text-sm ${
                    index === currentPhaseIndex
                      ? 'font-bold text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {PhaseDisplayNames[phase]}
                </span>
              </div>
              {index < phases.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    index < currentPhaseIndex ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('bom.tbdParts', 'TBD Parts')}</div>
          <div className={`text-2xl font-bold ${tbdParts.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {tbdParts.length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('bom.totalItems', 'Total Items')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {currentRevision?.items?.length || 0}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('bom.builds', 'Builds')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{builds.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">{t('bom.ecns', 'ECNs')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{ecns.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['bom', 'tbd', 'builds', 'ecn', 'history'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'bom' && t('bom.bomTab', 'BOM')}
              {tab === 'tbd' && `${t('bom.tbdTab', 'TBD Parts')} (${tbdParts.length})`}
              {tab === 'builds' && `${t('bom.buildsTab', 'Builds')} (${builds.length})`}
              {tab === 'ecn' && `${t('bom.ecnTab', 'ECN')} (${ecns.length})`}
              {tab === 'history' && t('bom.historyTab', 'History')}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {activeTab === 'bom' && (
          <div>
            {/* Add Part Button */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {currentRevision?.items?.length || 0} items in BOM
              </span>
              <button
                onClick={() => setShowAddPartModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('bom.addPart', 'Add Part')}
              </button>
            </div>
            {currentRevision ? (
              <BOMTable
                items={currentRevision.items}
                onDeleteItem={handleDeleteItem}
                deleting={deletingItem}
              />
            ) : (
              <div className="p-8 text-center text-gray-500">{t('bom.noRevision', 'No revision loaded')}</div>
            )}
          </div>
        )}
        {activeTab === 'tbd' && <TBDTable items={tbdParts} productId={productId!} revision={product.current_revision} />}
        {activeTab === 'builds' && <BuildsTable builds={builds} />}
        {activeTab === 'ecn' && <ECNTable ecns={ecns} />}
        {activeTab === 'history' && <PhaseHistory history={product.phase_history} />}
      </div>

      {/* Add Part Modal */}
      {/* Flow: Select Category -> Select Part Type (for electronic parts) -> Select Part -> Enter qty/ref/notes */}
      {showAddPartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Main Panel (Add Part Form) */}
            <div className="flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('bom.addPart', 'Add Part')}
                  </h3>
                  {/* Tab Navigation */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setAddPartModalTab('select')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        addPartModalTab === 'select'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t('bom.selectPart', 'Select Part')}
                    </button>
                    <button
                      onClick={() => setAddPartModalTab('digikey')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                        addPartModalTab === 'digikey'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      DigiKey Search
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 dark:bg-red-900/20 dark:border-red-800">
                    {error}
                  </div>
                )}

                {/* Select Part Tab Content */}
                {addPartModalTab === 'select' && (
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  {/* Step 1: Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('bom.category', 'Category')} *
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedPartType('');
                        setSelectedPart(null);
                        setPartFilterText(''); // Reset filter when category changes
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">{t('bom.selectCategory', 'Select Category...')}</option>
                      {partCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2: Part Type Filter (only for electronic_parts) */}
                  {selectedCategory === 'electronic_parts' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('bom.partType', 'Part Type')}
                      </label>
                      <select
                        value={selectedPartType}
                        onChange={(e) => {
                          setSelectedPartType(e.target.value);
                          setSelectedPart(null);
                          setPartFilterText(''); // Reset filter when part type changes
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">{t('bom.allTypes', 'All Types')}</option>
                        {partTypes.map((pt) => (
                          <option key={pt.part_code} value={pt.part_code}>
                            {pt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Step 3: Part Selection - Show as list for electronic_parts to include PCB preview button */}
                  {selectedCategory === 'electronic_parts' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('bom.selectPart', 'Select Part')} *
                        {loadingParts && <span className="ml-2 text-gray-400">(Loading...)</span>}
                      </label>
                      {/* Filter textbox for parts list */}
                      <div className="mb-2">
                        <input
                          type="text"
                          value={partFilterText}
                          onChange={(e) => setPartFilterText(e.target.value)}
                          placeholder={t('bom.filterParts', 'Filter by part number, description, or vendor...')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        />
                      </div>
                      {loadingParts ? (
                        <div className="text-center py-4 text-gray-500">
                          {t('common.loading', 'Loading...')}
                        </div>
                      ) : availableParts.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          {t('bom.noParts', 'No parts found')}
                        </div>
                      ) : (
                        <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                          {availableParts
                            .filter((part) => {
                              if (!partFilterText.trim()) return true;
                              const searchLower = partFilterText.toLowerCase();
                              return (
                                part.part_number?.toLowerCase().includes(searchLower) ||
                                part.vendor_product?.toLowerCase().includes(searchLower) ||
                                part.vendor_name?.toLowerCase().includes(searchLower) ||
                                part.vendor_spec?.toLowerCase().includes(searchLower) ||
                                part.brand?.toLowerCase().includes(searchLower)
                              );
                            })
                            .map((part) => (
                            <div
                              key={part.row_index}
                              className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0 ${
                                selectedPart?.part_number === part.part_number
                                  ? 'bg-blue-50 dark:bg-blue-900/30'
                                  : ''
                              }`}
                              onClick={() => setSelectedPart(part)}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {part.part_number}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {part.vendor_product || part.vendor_name}
                                </div>
                              </div>
                              {/* PCB Preview Button - only for PCB parts, disabled if no Gerber files */}
                              {isPCBPart(part.part_number) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (gerberAvailability[part.part_number] !== false) {
                                      handleViewPCB(part.part_number);
                                    }
                                  }}
                                  disabled={gerberAvailability[part.part_number] === false}
                                  className={`ml-2 px-2 py-1 text-xs rounded flex items-center gap-1 shrink-0 ${
                                    gerberAvailability[part.part_number] === false
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                      : 'bg-green-600 text-white hover:bg-green-700'
                                  }`}
                                  title={
                                    gerberAvailability[part.part_number] === false
                                      ? t('bom.pcbViewer.noGerberFiles', 'No Gerber files found')
                                      : t('bom.pcbViewer.previewPCB', 'Preview PCB')
                                  }
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                  </svg>
                                  PCB
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3: Part Selection - Dropdown for non-electronic parts */}
                  {selectedCategory && selectedCategory !== 'electronic_parts' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('bom.selectPart', 'Select Part')} *
                        {loadingParts && <span className="ml-2 text-gray-400">(Loading...)</span>}
                      </label>
                      <select
                        value={selectedPart?.part_number || ''}
                        onChange={(e) => {
                          const part = availableParts.find(p => p.part_number === e.target.value);
                          setSelectedPart(part || null);
                        }}
                        disabled={loadingParts}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                      >
                        <option value="">
                          {loadingParts
                            ? t('common.loading', 'Loading...')
                            : availableParts.length === 0
                              ? t('bom.noParts', 'No parts found')
                              : t('bom.selectPartPlaceholder', `Select from ${availableParts.length} parts...`)}
                        </option>
                        {availableParts.map((part) => (
                          <option key={part.row_index} value={part.part_number}>
                            {part.part_number} - {part.vendor_product || part.vendor_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Selected Part Details (read-only, pre-filled from sheet) */}
                  {selectedPart && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('bom.partDetails', 'Part Details')} ({t('bom.fromSheet', 'from Google Sheet')})
                        </h4>
                        {/* PCB Preview button in part details for selected PCB part */}
                        {isPCBPart(selectedPart.part_number) && (
                          <button
                            onClick={() => {
                              if (gerberAvailability[selectedPart.part_number] !== false) {
                                handleViewPCB(selectedPart.part_number);
                              }
                            }}
                            disabled={gerberAvailability[selectedPart.part_number] === false}
                            className={`px-3 py-1 text-sm rounded-lg flex items-center gap-1 ${
                              gerberAvailability[selectedPart.part_number] === false
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                            title={
                              gerberAvailability[selectedPart.part_number] === false
                                ? t('bom.pcbViewer.noGerberFiles', 'No Gerber files found')
                                : t('bom.pcbViewer.previewPCB', 'Preview PCB')
                            }
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                            {t('bom.pcbViewer.previewPCB', 'Preview PCB')}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('bom.partCode', 'Part Code')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100 font-medium">{selectedPart.part_number}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('bom.brand', 'Brand')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedPart.brand || '-'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500 dark:text-gray-400">{t('bom.description', 'Description')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedPart.vendor_product || '-'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500 dark:text-gray-400">{t('bom.specification', 'Spec')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedPart.vendor_spec || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('bom.vendor', 'Vendor')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedPart.vendor_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">MPQ:</span>
                          <span className="ml-2 text-gray-900 dark:text-gray-100">{selectedPart.mpq || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  {selectedPart && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {t('bom.bomDetails', 'BOM Details')}
                      </h4>
                    </div>
                  )}

                  {/* User Input: Qty, Unit, Ref Designator */}
                  {selectedPart && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('bom.qty', 'Qty')} *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={newPartInput.qty_per}
                          onChange={(e) => setNewPartInput({ ...newPartInput, qty_per: parseFloat(e.target.value) || 1 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('bom.unit', 'Unit')}
                        </label>
                        <select
                          value={newPartInput.unit}
                          onChange={(e) => setNewPartInput({ ...newPartInput, unit: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="pcs">pcs</option>
                          <option value="ea">ea</option>
                          <option value="set">set</option>
                          <option value="m">m</option>
                          <option value="cm">cm</option>
                          <option value="mm">mm</option>
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                          <option value="ml">ml</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('bom.refDesignator', 'Ref Designator')}
                        </label>
                        <input
                          type="text"
                          value={newPartInput.ref_designator}
                          onChange={(e) => setNewPartInput({ ...newPartInput, ref_designator: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder="e.g., R1, C1, U1"
                        />
                      </div>
                    </div>
                  )}

                  {/* User Input: Status & Notes */}
                  {selectedPart && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('bom.partStatus', 'Part Status')}
                        </label>
                        <select
                          value={newPartInput.part_status}
                          onChange={(e) => setNewPartInput({ ...newPartInput, part_status: e.target.value as PartStatus })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="tbd">TBD</option>
                          <option value="preliminary">Preliminary</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="approved">Approved</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('bom.notes', 'Notes')}
                        </label>
                        <input
                          type="text"
                          value={newPartInput.notes}
                          onChange={(e) => setNewPartInput({ ...newPartInput, notes: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          placeholder={t('bom.notesPlaceholder', 'Optional notes...')}
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* DigiKey Search Tab Content */}
                {addPartModalTab === 'digikey' && (
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('bom.digikey.searchDescription', 'Search DigiKey for parts and batch import them to Google Sheets.')}
                  </p>

                  {/* Part Type Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('bom.partType', 'Part Type')}
                    </label>
                    {loadingDigikeyPartTypes ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('common.loading', 'Loading...')}
                      </div>
                    ) : (
                      <select
                        value={selectedDigikeyPartType?.part_code || ''}
                        onChange={(e) => {
                          const pt = digikeyPartTypes.find(t => t.part_code === e.target.value);
                          setSelectedDigikeyPartType(pt || null);
                          // Clear previous search when changing type
                          setDigikeySearchResults([]);
                          setSelectedDigikeyParts([]);
                          setBatchImportResult(null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        {digikeyPartTypes.map((pt) => (
                          <option key={pt.part_code} value={pt.part_code}>
                            {pt.label} ({pt.part_type_en})
                            {pt.digikey_category ? '' : ' - No DigiKey mapping'}
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedDigikeyPartType && !selectedDigikeyPartType.digikey_category && (
                      <p className="text-xs text-amber-500 mt-1">
                        {t('bom.digikey.noMapping', 'This part type has no DigiKey category mapping. Search will use raw text.')}
                      </p>
                    )}
                  </div>

                  {/* Search Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {selectedDigikeyPartType?.part_type_en || 'Part'} {t('bom.digikey.specifications', 'Specifications')}
                    </label>
                    <textarea
                      value={digikeySearchInput}
                      onChange={(e) => setDigikeySearchInput(e.target.value)}
                      placeholder={selectedDigikeyPartType?.placeholder
                        ? `Enter specs, one per line. Example:\n${selectedDigikeyPartType.placeholder}`
                        : 'Enter part specifications or MPN, one per line'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      rows={4}
                    />
                    {selectedDigikeyPartType?.format && (
                      <p className="text-xs text-gray-400 mt-1">
                        {t('bom.digikey.format', 'Format')}: {selectedDigikeyPartType.format}
                      </p>
                    )}
                  </div>

                  {/* Smart Parse (AI) Toggle */}
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useAIParse}
                        onChange={(e) => setUseAIParse(e.target.checked)}
                        disabled={!aiEnabled}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 rounded-full peer ${
                        aiEnabled
                          ? 'bg-gray-200 peer-checked:bg-purple-600 dark:bg-gray-600 peer-checked:dark:bg-purple-600'
                          : 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                      } peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full`}></div>
                    </label>
                    <span className={`text-sm font-medium ${aiEnabled ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                      {t('bom.digikey.smartParse', 'Smart Parse (AI)')}
                    </span>
                    {!aiEnabled && (
                      <span className="text-xs text-gray-400">
                        ({t('bom.digikey.aiNotConfigured', 'AI not configured')})
                      </span>
                    )}
                    {useAIParse && aiEnabled && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                        </svg>
                        Claude
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleDigikeySearch}
                    disabled={digikeySearching || !digikeySearchInput.trim()}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {digikeySearching ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Searching...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Search DigiKey
                      </>
                    )}
                  </button>

                  {/* Error Display */}
                  {digikeySearchError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 dark:bg-red-900/20 dark:border-red-800">
                      {digikeySearchError}
                    </div>
                  )}

                  {/* Web Search Button - shown when DigiKey returns 0 results and smart parse is enabled */}
                  {hasZeroDigikeyResults &&
                   useAIParse &&
                   aiEnabled &&
                   !webSearchResult && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:border-amber-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-amber-700 dark:text-amber-300 font-medium">
                            {t('bom.digikey.noDigikeyResults', 'No DigiKey results found')}
                          </div>
                          <div className="text-sm text-amber-600 dark:text-amber-400">
                            {t('bom.digikey.tryAISearch', 'Try AI-powered search to find alternative parts from manufacturer catalogs')}
                          </div>
                        </div>
                        <button
                          onClick={handleWebSearch}
                          disabled={webSearching}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {webSearching ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('bom.digikey.searchingAI', 'Searching...')}
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                              {t('bom.digikey.searchOnlineAI', 'Search Online with AI')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Parse Results (when AI parsing is used) */}
                  {parseResults.length > 0 && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg dark:bg-purple-900/20 dark:border-purple-800">
                      <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                        {t('bom.digikey.parseResults', 'Parse Results')}
                      </h4>
                      <div className="space-y-2">
                        {parseResults.map((result, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            {/* Method indicator */}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              result.method === 'ai'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                                : result.method === 'structured'
                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}>
                              {result.method === 'ai' ? 'AI' : result.method === 'structured' ? 'Regex' : 'Raw'}
                            </span>
                            {/* Confidence */}
                            <span className={`text-xs ${
                              result.confidence >= 0.9 ? 'text-green-600 dark:text-green-400' :
                              result.confidence >= 0.7 ? 'text-amber-600 dark:text-amber-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {Math.round(result.confidence * 100)}%
                            </span>
                            {/* Original -> Search Query */}
                            <span className="text-gray-600 dark:text-gray-400">
                              <span className="font-mono">{result.original}</span>
                              {result.search_query !== result.original && (
                                <>
                                  <span className="mx-1">→</span>
                                  <span className="font-mono text-purple-600 dark:text-purple-400">{result.search_query}</span>
                                </>
                              )}
                            </span>
                            {/* Notes */}
                            {result.notes && (
                              <span className="text-xs text-gray-400 italic">({result.notes})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Batch Import Result */}
                  {batchImportResult && (
                    <div className={`p-4 rounded-lg ${batchImportResult.success > 0 ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800'}`}>
                      <div className="font-medium">
                        Import Result: {batchImportResult.success} / {batchImportResult.total} parts added to Google Sheet
                      </div>
                      {batchImportResult.errors && batchImportResult.errors.length > 0 && (
                        <div className="mt-2 text-sm">
                          Errors:
                          <ul className="list-disc list-inside">
                            {batchImportResult.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Web Search Results */}
                  {webSearchResult && (
                    <div className="space-y-4">
                      {/* Prompt Display (Collapsible) */}
                      {webSearchResult.prompt && (
                        <details className="p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
                          <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                            {t('bom.digikey.promptSentToAI', 'Prompt Sent to Claude AI')}
                          </summary>
                          <pre className="mt-3 p-3 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono">
                            {webSearchResult.prompt}
                          </pre>
                        </details>
                      )}

                      {/* Results Header */}
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg dark:bg-purple-900/20 dark:border-purple-800">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-600 text-white">
                              AI
                            </span>
                            <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300">
                              {t('bom.digikey.aiSuggestedParts', 'AI Suggested Parts')}
                            </h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              webSearchResult.confidence >= 0.8 ? 'bg-green-100 text-green-700 dark:bg-green-800/50 dark:text-green-300' :
                              webSearchResult.confidence >= 0.6 ? 'bg-amber-100 text-amber-700 dark:bg-amber-800/50 dark:text-amber-300' :
                              'bg-red-100 text-red-700 dark:bg-red-800/50 dark:text-red-300'
                            }`}>
                              {Math.round(webSearchResult.confidence * 100)}% confidence
                            </span>
                          </div>
                          <button
                            onClick={() => setWebSearchResult(null)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        {/* AI Explanation */}
                        <p className="text-sm text-purple-600 dark:text-purple-400 mb-3">
                          {webSearchResult.ai_explanation}
                        </p>
                        {/* Warning Banner */}
                        <div className="p-2 bg-amber-100 border border-amber-200 rounded text-xs text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300">
                          ⚠️ {t('bom.digikey.aiWarning', 'These parts are AI suggestions. Verify specifications before ordering.')}
                        </div>
                      </div>

                      {/* Parts List */}
                      {webSearchResult.parts.length > 0 ? (
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Select</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">P/N</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Manufacturer</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Est. Price</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Confidence</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                              {webSearchResult.parts.map((part, idx) => (
                                <tr key={idx} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                  selectedWebParts.some(p => p.manufacturer_pn === part.manufacturer_pn)
                                    ? 'bg-purple-50 dark:bg-purple-900/20'
                                    : ''
                                }`}>
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedWebParts.some(p => p.manufacturer_pn === part.manufacturer_pn)}
                                      onChange={() => toggleWebPartSelection(part)}
                                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100">
                                    {part.manufacturer_pn}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    {part.manufacturer}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    {part.description}
                                    {part.alternatives && part.alternatives.length > 0 && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        Alt: {part.alternatives.slice(0, 2).join(', ')}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    {part.estimated_price || 'N/A'}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      part.confidence >= 0.9 ? 'bg-green-100 text-green-700' :
                                      part.confidence >= 0.7 ? 'bg-amber-100 text-amber-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {Math.round(part.confidence * 100)}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          {t('bom.digikey.noAISuggestions', 'No AI suggestions found for this specification')}
                        </div>
                      )}

                      {/* Add Selected Button */}
                      {selectedWebParts.length > 0 && (
                        <div className="flex justify-end">
                          <button
                            onClick={handleAddWebSearchParts}
                            disabled={batchImporting}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                          >
                            {t('bom.digikey.addSelectedAI', `Add ${selectedWebParts.length} AI-Suggested Part(s)`)}
                          </button>
                        </div>
                      )}

                      {/* Sources */}
                      {webSearchResult.sources.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Sources: {webSearchResult.sources.join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search Results */}
                  {digikeySearchResults.length > 0 && (() => {
                    // Get all unique manufacturers from all results
                    const allProducts = digikeySearchResults.flatMap(g => g.products);
                    const manufacturers = Array.from(new Set(allProducts.map(p => p.manufacturer_name))).sort();

                    // Filter products by selected manufacturer
                    const getFilteredProducts = (products: DigiKeyMLCCResult[]) => {
                      if (selectedManufacturer === 'all') return products;
                      return products.filter(p => p.manufacturer_name === selectedManufacturer);
                    };

                    // Count products per manufacturer
                    const manufacturerCounts = manufacturers.reduce((acc, mfr) => {
                      acc[mfr] = allProducts.filter(p => p.manufacturer_name === mfr).length;
                      return acc;
                    }, {} as Record<string, number>);

                    return (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Search Results ({selectedDigikeyParts.length} selected)
                        </h4>
                        {selectedDigikeyParts.length > 0 && (
                          <button
                            onClick={handleBatchImport}
                            disabled={batchImporting}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {batchImporting ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Importing...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add {selectedDigikeyParts.length} to Sheet
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Manufacturer Filter Tabs */}
                      {manufacturers.length > 1 && (
                        <div className="flex flex-wrap gap-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => setSelectedManufacturer('all')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                              selectedManufacturer === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            All ({allProducts.length})
                          </button>
                          {manufacturers.map((mfr) => (
                            <button
                              key={mfr}
                              onClick={() => setSelectedManufacturer(mfr)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                                selectedManufacturer === mfr
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              {mfr} ({manufacturerCounts[mfr]})
                            </button>
                          ))}
                        </div>
                      )}

                      {digikeySearchResults.map((group, groupIndex) => {
                        const filteredProducts = getFilteredProducts(group.products);
                        if (filteredProducts.length === 0 && selectedManufacturer !== 'all') return null;

                        return (
                        <div key={groupIndex} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {group.input}
                              </span>
                              <span className="text-sm text-gray-500">
                                {filteredProducts.length} results
                                {selectedManufacturer !== 'all' && ` (${group.products.length} total)`}
                              </span>
                            </div>
                            {group.error && (
                              <p className="text-sm text-red-500 mt-1">{group.error}</p>
                            )}
                          </div>

                          {filteredProducts.length > 0 && (
                            <div className="max-h-48 overflow-y-auto">
                              {filteredProducts.map((product, productIndex) => (
                                <div
                                  key={productIndex}
                                  onClick={() => toggleDigikeyPartSelection(product)}
                                  className={`flex items-center px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                                    isDigikeyPartSelected(product) ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isDigikeyPartSelected(product)}
                                    onChange={() => {}}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                  />
                                  <div className="ml-3 flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                        {product.manufacturer_part_number}
                                      </span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        ({product.manufacturer_name})
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {product.description}
                                    </p>
                                  </div>
                                  <div className="ml-3 text-right">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      ${product.unit_price.toFixed(4)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {product.quantity_available.toLocaleString()} avail
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )})}
                    </div>
                  );})()}
                </div>
                )}

                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      resetAddPartForm();
                      setShowAddPartModal(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={handleAddPart}
                    disabled={addingPart || !selectedPart}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingPart ? t('common.adding', 'Adding...') : t('bom.addPart', 'Add Part')}
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}

      {/* PCB Gerber Viewer Modal - separate from Add Part modal */}
      {pcbPartNumber && (
        <PCBViewer
          partNumber={pcbPartNumber}
          isOpen={showPCBModal}
          onClose={handleClosePCBModal}
          embedded={false}
        />
      )}

      {/* BOM Import Dialog */}
      {productId && (
        <BOMImportDialog
          isOpen={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          productId={productId}
          currentRevision={currentRevision?.revision}
          revisions={revisions}
          onImportComplete={async () => {
            // Refresh revision data after import
            if (productId) {
              try {
                const [revisionsData, tbdData] = await Promise.all([
                  listRevisions(productId),
                  getTBDParts(productId),
                ]);
                setRevisions(revisionsData);
                setTbdParts(tbdData);
                // Reload current revision if it was updated
                if (currentRevision) {
                  const updated = await getRevision(productId, currentRevision.revision);
                  setCurrentRevision(updated);
                } else if (revisionsData.length > 0) {
                  // Load the first revision if there was no current revision before
                  const firstRev = await getRevision(productId, revisionsData[0].revision);
                  setCurrentRevision(firstRev);
                }
              } catch (err) {
                logger.error('Failed to refresh after import:', err);
              }
            }
          }}
        />
      )}

      {/* AI Web Search Confirmation Dialog */}
      {showWebSearchConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('bom.digikey.confirmAIParts', 'Confirm AI-Suggested Parts')}
            </h3>

            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:border-amber-700">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                ⚠️ {t('bom.digikey.aiConfirmWarning', 'These parts were found via AI search, not DigiKey.')}
              </p>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={webSearchConfirmChecks.verified}
                  onChange={(e) => setWebSearchConfirmChecks(prev => ({ ...prev, verified: e.target.checked }))}
                  className="h-4 w-4 mt-0.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('bom.digikey.checkVerified', 'I have verified the specifications match my requirements')}
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowWebSearchConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmAddWebSearchParts}
                disabled={!webSearchConfirmChecks.verified || batchImporting}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {batchImporting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    {t('common.adding', 'Adding...')}
                  </span>
                ) : (
                  t('bom.digikey.addToSheet', 'Add to Sheet')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// BOM Table Component
const BOMTable: React.FC<{
  items: BOMItem[];
  onDeleteItem?: (lineNo: number) => void;
  deleting?: number | null;
}> = ({ items, onDeleteItem, deleting }) => {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return <div className="p-8 text-center text-gray-500">{t('bom.noItems', 'No BOM items')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Part Code</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Package</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Spec</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Brand</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Digikey</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qty</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Ref Des</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
            {onDeleteItem && (
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => (
            <tr key={item.line_no} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100">{item.line_no}</td>
              <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                {item.is_phantom && <span className="text-gray-400 mr-1" title="Phantom - explodes through">👻</span>}
                {item.is_assembly && '+ '}
                {item.part_code}
              </td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={item.description}>
                {item.description}
              </td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.part_type || '-'}</td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.package_size || '-'}</td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[150px] truncate" title={item.specification}>
                {item.specification || '-'}
              </td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.brand || '-'}</td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                {item.digikey_pn ? (
                  <a
                    href={`https://www.digikey.com/en/products/detail/-/-/${item.digikey_pn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {item.digikey_pn}
                  </a>
                ) : '-'}
              </td>
              <td className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100">{item.qty_per} {item.unit}</td>
              <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{item.ref_designator || '-'}</td>
              <td className="px-3 py-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${PartStatusColors[item.part_status]}`}>
                  {PartStatusDisplayNames[item.part_status]}
                </span>
              </td>
              {onDeleteItem && (
                <td className="px-3 py-3">
                  <button
                    onClick={() => onDeleteItem(item.line_no)}
                    disabled={deleting === item.line_no}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('common.delete', 'Delete')}
                  >
                    {deleting === item.line_no ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// TBD Parts Table Component
const TBDTable: React.FC<{ items: BOMItem[]; productId: string; revision: string }> = ({
  items,
  // productId,
  // revision,
}) => {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return (
      <div className="p-8 text-center text-green-600">
        {t('bom.noTBDParts', 'All parts are confirmed - no TBD items')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Part Code</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qty</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Notes</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {items.map((item) => (
            <tr key={item.line_no} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.line_no}</td>
              <td className="px-4 py-3 text-sm font-medium text-red-600">{item.part_code || 'TBD'}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{item.description}</td>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.qty_per}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{item.notes || '-'}</td>
              <td className="px-4 py-3">
                <button className="text-blue-600 hover:underline text-sm">
                  {t('bom.resolve', 'Resolve')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Builds Table Component
const BuildsTable: React.FC<{ builds: Build[] }> = ({ builds }) => {
  const { t } = useTranslation();

  if (!builds || builds.length === 0) {
    return <div className="p-8 text-center text-gray-500">{t('bom.noBuilds', 'No builds recorded')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Build ID</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Phase</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Revision</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Qty</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Issues</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {builds.map((build) => (
            <tr key={build.build_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{build.build_id}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{PhaseDisplayNames[build.phase]}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{build.revision}</td>
              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                {build.actual_qty} / {build.planned_qty}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  build.status === 'completed' ? 'bg-green-100 text-green-800' :
                  build.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {build.status}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {build.issues?.length || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ECN Table Component
const ECNTable: React.FC<{ ecns: ECN[] }> = ({ ecns }) => {
  const { t } = useTranslation();

  if (!ecns || ecns.length === 0) {
    return <div className="p-8 text-center text-gray-500">{t('bom.noECNs', 'No ECNs')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">ECN #</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Title</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reason</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Requested</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {ecns.map((ecn) => (
            <tr key={ecn.ecn_number} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{ecn.ecn_number}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{ecn.title}</td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{ecn.reason}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  ecn.status === 'approved' ? 'bg-green-100 text-green-800' :
                  ecn.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  ecn.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {ecn.status}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {new Date(ecn.requested_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Phase History Component
const PhaseHistory: React.FC<{ history: Product['phase_history'] }> = ({ history }) => {
  const { t } = useTranslation();

  if (!history || history.length === 0) {
    return <div className="p-8 text-center text-gray-500">{t('bom.noHistory', 'No phase history')}</div>;
  }

  return (
    <div className="p-6">
      <div className="relative">
        {history.map((entry, index) => (
          <div key={index} className="flex items-start mb-6">
            <div className={`w-4 h-4 rounded-full ${PhaseColors[entry.phase]} mt-1`} />
            <div className="ml-4">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {PhaseDisplayNames[entry.phase]}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Entered: {new Date(entry.entered_at).toLocaleDateString()}
                {entry.exited_at && ` - Exited: ${new Date(entry.exited_at).toLocaleDateString()}`}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Revision: {entry.revision_at_entry} | Approved by: {entry.approved_by}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductDetail;
